import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'context/AuthContext';
import type { ProductDocument } from 'models/product';
import { client } from '@util/getTypesense';
import useSearchHistory from '@util/hooks/useSearchHistory';
import { cleanLocation } from '@util/firestore/products';
import { logError } from '@util/logError';

export const useRecentlySearched = () => {
  const { userDoc } = useAuth();
  const searchHistory = useSearchHistory({ uid: userDoc?.uid });
  const nonEmptySearches = searchHistory.filter((s) => s !== '');
  const mostRecentTerm = nonEmptySearches[nonEmptySearches.length - 1];

  const { data: recentlySearchedProducts = [], isLoading } = useQuery({
    queryKey: ['recentlySearchedProducts', userDoc?.uid, searchHistory],
    queryFn: async () => {
      if (!searchHistory.length) return [];
      const recentTerms = [...searchHistory]
        .filter((s) => s !== '')
        .reverse()
        .slice(0, 1);

      const searches = recentTerms.map((term) => ({
        collection: 'products_alias',
        q: term,
        preset: 'main_search',
        per_page: 10,
        filter_by: 'category:!="Dirt Bikes"',
      }));

      try {
        const response = await client.multiSearch.perform<ProductDocument[]>({
          searches,
        });

        const products = response.results
          .flatMap(
            (result, index) =>
              result.hits?.map((hit) => ({
                ...hit.document,
                searchTerm: recentTerms[index],
              })) ?? []
          )
          .filter(
            (product, index, arr) =>
              arr.findIndex((p) => p.id === product.id) === index
          )
          .slice(0, 20);

        return products.map((product) => cleanLocation(product));
      } catch (error) {
        logError(error, 'Error fetching recently searched products:');
        return [];
      }
    },
    enabled: !!userDoc?.uid && searchHistory.length > 0,
    staleTime: 1000 * 60 * 10,
  });

  return {
    recentlySearchedProducts,
    isLoading,
    mostRecentTerm,
  };
};
